import { TextField, Select, SelectEvent } from "@king-ict/eupisi-ui/components";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import { AlternativeDocumentInfo } from "./AlternativeDocumentInfo";
import { useParentCodebooks, usePersonOib, FetchedDiffValue } from "./index";
import { FormCard, InfoPanel } from "../../../../components";
import {
  AddressDisplay,
  SectionHeader,
} from "../../../../components/FormControls";
import { PersonDetailsWithNullPerson } from "../../../../models/Request/RegistrationFormDataChange";
import { PersonIdentification } from "../../../../models/Shared/PersonIdentification";

interface IProps {
  parentDetails: PersonDetailsWithNullPerson;
  parentIdentification?: PersonIdentification;
  onUpdate: (updatedDetails: PersonDetailsWithNullPerson) => void;
}

export const SecondParentAccordion = ({
  parentDetails,
  parentIdentification,
  onUpdate,
}: IProps) => {
  const { t } = useTranslation();
  const { parentshipTypes } = useParentCodebooks();
  const {
    isLoading,
    fetchPersonDetailsByOib,
    canApply,
    applyFetchedData,
    data: fetchedData,
    successMessage,
  } = usePersonOib();

  const {
    firstName,
    lastName,
    dateOfBirth,
    parentshipTypeId,
    qualificationName,
    phoneNumber,
    email,
    residence,
  } = parentDetails;

  const handleUpdate = (value: Partial<PersonDetailsWithNullPerson>) => {
    onUpdate({ ...parentDetails, ...value });
  };

  const userOib = parentDetails.person?.oib ?? parentIdentification?.oib ?? "";

  const handleRefresh = () => {
    if (!userOib) return;
    fetchPersonDetailsByOib(userOib);
  };

  const handleApply = () => {
    const fetched = applyFetchedData();
    if (fetched) {
      const updated: PersonDetailsWithNullPerson = {
        ...fetched,
        parentshipTypeId: parentDetails.parentshipTypeId,
        email: parentDetails.email,
        phoneNumber: parentDetails.phoneNumber,
        qualificationName: parentDetails.qualificationName,
      };
      onUpdate(updated);
    }
  };

  return (
    <FormCard className="position-relative">
      {successMessage && (
        <InfoPanel color="success" fullWidth>
          {t(successMessage)}
        </InfoPanel>
      )}
      <SectionHeader
        title={t("person.personalInformation")}
        actionButton={{
          canApply,
          handleApply,
          handleRefresh,
          isLoading,
          isHidden: !userOib,
        }}
      />
      <AlternativeDocumentInfo
        personIdentification={parentIdentification}
        userOib={userOib}
      />
      <Row className="mb-2">
        <Col md={3}>
          <TextField
            label={t("person.firstName")}
            value={firstName}
            readonly
            plaintext
          />
          <FetchedDiffValue
            show={
              canApply && !!fetchedData && fetchedData.firstName !== firstName
            }
          >
            {fetchedData?.firstName}
          </FetchedDiffValue>
        </Col>
        <Col md={3}>
          <TextField
            label={t("person.lastName")}
            value={lastName}
            readonly
            plaintext
          />
          <FetchedDiffValue
            show={
              canApply && !!fetchedData && fetchedData.lastName !== lastName
            }
          >
            {fetchedData?.lastName}
          </FetchedDiffValue>
        </Col>
        <Col md={3}>
          <TextField
            label={t("person.oib")}
            value={userOib}
            readonly
            plaintext
          />
          <FetchedDiffValue
            show={
              canApply && !!fetchedData && fetchedData.person?.oib !== userOib
            }
          >
            {fetchedData?.person?.oib}
          </FetchedDiffValue>
        </Col>
        <Col md={3}>
          <TextField
            label={t("person.yearOfBirth")}
            value={dateOfBirth ? new Date(dateOfBirth).getFullYear() : ""}
            readonly
            plaintext
          />
          <FetchedDiffValue
            show={
              canApply &&
              !!fetchedData &&
              fetchedData.dateOfBirth !== dateOfBirth
            }
          >
            {fetchedData?.dateOfBirth
              ? new Date(fetchedData.dateOfBirth).getFullYear()
              : ""}
          </FetchedDiffValue>
        </Col>
      </Row>
      <Row className="mb-2">
        <Col md={3}>
          <TextField
            label={t("person.phoneNumber")}
            value={phoneNumber ?? ""}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleUpdate({ phoneNumber: e.target.value })
            }
            maxLength={30}
          />
        </Col>
        <Col md={3}>
          <Select
            label={t("person.parentshipType")}
            value={parentshipTypeId ?? null}
            selectData={{
              keyProp: "id",
              valueProp: "id",
              textProp: "name",
              data: parentshipTypes ?? [],
            }}
            onChange={(e: React.ChangeEvent<SelectEvent>) =>
              handleUpdate({ parentshipTypeId: Number(e.target.value) })
            }
            required
          />
        </Col>
        <Col md={3}>
          <TextField
            label={t("person.email")}
            value={email ?? ""}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleUpdate({ email: e.target.value })
            }
            maxLength={254}
          />
        </Col>
        <Col md={3}>
          <TextField
            label={t("person.qualification")}
            value={qualificationName ?? ""}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleUpdate({ qualificationName: e.target.value })
            }
            maxLength={255}
          />
        </Col>
      </Row>
      <SectionHeader
        title={t("person.permanentResidenceInformation")}
        subtitle={t("person.mupSource")}
      />
      <AddressDisplay
        address={residence?.permanentAddress}
        fetchedAddress={fetchedData?.residence?.permanentAddress}
        canApply={canApply}
        title={t("person.permanentResidenceInformation")}
      />
      <SectionHeader
        title={t("person.temporaryResidenceInformation")}
        subtitle={t("person.mupSource")}
      />
      <AddressDisplay
        address={residence?.temporaryAddress}
        fetchedAddress={fetchedData?.residence?.temporaryAddress}
        canApply={canApply}
        title={t("person.temporaryResidenceInformation")}
      />
    </FormCard>
  );
};
